import { NetworkStatus } from '@apollo/client';
import { ErrorRetry, useReportList } from '@bechtel/client-common';
import { Box, Heading, Icon, IconSize, Spinner, SpinnerSize } from '@bechtel/gds';
import { ReportList, SortOrder } from '@bechtel/types';
import { useLingui } from '@lingui/react';
import { get, map } from 'lodash';
import { FC, useEffect } from 'react';
import {
  ExpandCollapseBorderBoxStyled,
  ExpandCollapseBoxStyled,
  ListContainerStyled,
  MenuItemStyled,
  NavLinkStyled,
  ReportListCollapsedStyled,
  ReportListContainerStyled,
  ReportListStyled,
} from './styled';
import { ReportsListProps } from './types';
export const ReportsList: FC<ReportsListProps> = ({ isListCollapsed, onToggleCollapse }) => {
  const { i18n } = useLingui();
  const [loadReports, { loading, error, data, fetchMore, networkStatus, called, refetch }] = useReportList();
  const reportsLimit = 20;
  useEffect(() => {
    loadReports({
      variables: {
        paging: {
          cursor: '1',
          limit: reportsLimit,
        },
        input: {
          searchKeyword: '',
          activeReports: true,
          sortOrder: SortOrder.ASC,
        },
      },
    });
  }, [loadReports]);

  const reportList: ReportList['items'] = get(data, 'reports.items', []);
  const totalPages = Math.ceil(get(data, 'reports.total', 1) / reportsLimit);
  const reportListCursor = get(data, 'reports.cursor', 0);

  const handlScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      reportListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: reportListCursor.toString(),
            limit: reportsLimit,
          },
        },
      });
    }
  };
  return (
    <ListContainerStyled isListCollapsed={isListCollapsed}>
      <ReportListContainerStyled isListCollapsed={isListCollapsed}>
        <ReportListStyled
          onScroll={(e) => {
            handlScroll(e);
          }}
        >
          {!error &&
            !loading &&
            map(reportList, (item) => {
              return (
                <NavLinkStyled to={`/reports/${item.id}`} key={item.id}>
                  <MenuItemStyled>{item.name}</MenuItemStyled>
                </NavLinkStyled>
              );
            })}
          {!loading && error && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              flexDirection={{ sm: 'column' }}
              p={{ sm: '500' }}
              data-testid="report-list-retry"
            >
              <ErrorRetry refetch={refetch}>
                {i18n._(`There was a problem loading reports name. Please click the button below to try again.`)}
              </ErrorRetry>
            </Box>
          )}
          {!loading && !error && reportList?.length === 0 && (
            <Box
              flexDirection={{ sm: 'column' }}
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              style={{ width: '100%', height: '100%' }}
              p={{ sm: '500' }}
              data-testid="report-list-empty"
            >
              <Heading size="400" weight="600" color="grey.600">
                {i18n._(`No report found.`)}
              </Heading>
            </Box>
          )}
          {(!called || networkStatus === NetworkStatus.refetch || loading) && (
            <Box
              alignItems={{ sm: 'center' }}
              justifyContent={{ sm: 'center' }}
              flexGrow={{ sm: 1 }}
              p={{ sm: '500' }}
              data-testid="report-list-loading"
            >
              <Spinner size={SpinnerSize.LARGE} />
            </Box>
          )}
        </ReportListStyled>
      </ReportListContainerStyled>
      <ReportListCollapsedStyled isListCollapsed={isListCollapsed}></ReportListCollapsedStyled>
      <ExpandCollapseBorderBoxStyled isListCollapsed={isListCollapsed}>
        <ExpandCollapseBoxStyled onClick={onToggleCollapse}>
          <Icon icon={isListCollapsed ? 'chevron-right' : 'chevron-left'} size={IconSize.MEDIUM} fillColor="blue.800" />
        </ExpandCollapseBoxStyled>
      </ExpandCollapseBorderBoxStyled>
    </ListContainerStyled>
  );
};
